/**
 * useBrokenImage — tracks whether an <img> failed to load (404 / corrupt / decode error).
 *
 * Flow:
 *   - Returns `showImage` (render the <img>) and `onError` (wire to <img onError>).
 *   - `broken` flips true once the browser reports a load failure.
 *   - Resets whenever `url` changes — list rows are reused across pages/refetches,
 *     and a re-uploaded photo must clear a stale "broken" mark.
 */
import { useState } from "react";

export function useBrokenImage(url?: string | null) {
  const [broken, setBroken] = useState(false);

  // Reset during render when the url changes (React's documented "adjusting state
  // on prop change" pattern) — avoids a setState-in-effect round-trip.
  const [prevUrl, setPrevUrl] = useState(url);
  if (url !== prevUrl) {
    setPrevUrl(url);
    setBroken(false);
  }

  return {
    /** True while a valid url is present and has not failed to load. */
    showImage: Boolean(url) && !broken,
    /** Image failed to load. */
    broken,
    /** Wire to the element's `onError`. */
    onError: () => setBroken(true),
  };
}
